import { createContext, useContext } from "react";

import useLocalStorageState, { LOCAL_STORAGE_KEYS } from "@/hooks/useLocalStorageState";

/**
 * "local" = browser's local timezone (default).
 * Any other value is an IANA timezone string, e.g. "UTC", "America/New_York".
 */
export type TimezoneValue = "local" | string;

interface TimezoneContextValue {
  timezone: TimezoneValue;
  setTimezone: (tz: TimezoneValue) => void;
}

const TimezoneContext = createContext<TimezoneContextValue | null>(null);

export const TimezoneProvider = ({ children }: { children: React.ReactNode }) => {
  const [timezone, setTimezone] = useLocalStorageState<TimezoneValue>(
    LOCAL_STORAGE_KEYS.REQUESTS_TIMEZONE,
    "local",
  );

  return (
    <TimezoneContext.Provider value={{ timezone, setTimezone }}>
      {children}
    </TimezoneContext.Provider>
  );
};

export const useTimezone = (): TimezoneContextValue => {
  const ctx = useContext(TimezoneContext);
  if (!ctx) {
    // Outside provider: fall back gracefully to local time
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    return { timezone: "local", setTimezone: () => {} };
  }
  return ctx;
};
